import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import { apiClient } from "../lib/apiClient";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../i18n/I18nProvider";
import { errorMessage } from "../i18n/apiErrors";
import AppHead from "../components/AppHead";
import PageWrap from "../components/PageWrap";
import Panel from "../components/Panel";

// ---------------------------------------------------------------------------
// Form error state
// ---------------------------------------------------------------------------

/**
 * Fehler werden als sprach-unabhängiges Objekt gehalten und erst beim Rendern
 * aufgelöst (Req 12.6):
 * - lokale Meldungen → `{ kind: "key" }` mit einem Katalog-Schlüssel
 * - API-Fehler → `{ kind: "api" }` mit dem Fehler-OBJEKT (via errorMessage)
 */
type DisplayError =
  | { kind: "key"; key: string }
  | { kind: "api"; err: unknown };

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Group {
  id: string;
  name: string;
  invitationCode?: string;
  createdBy?: string;
  createdAt?: string;
}

interface GroupsResponse {
  groups: Group[];
}

/** A child belonging to the current user. */
interface Child {
  id: string;
  ownerId: string;
  name: string;
  createdAt: string;
  deletedAt: string | null;
}

/** Response from GET /api/children */
interface ChildrenResponse {
  children: Child[];
}

/** Response from POST/PATCH /api/children */
interface ChildResponse {
  child: Child;
}

/** One group's resolved email preference, as returned by the Preference_API. */
interface PreferenceItem {
  groupId: string;
  emailEnabled: boolean;
}

/** Response from GET /api/notification-preferences */
interface PreferencesResponse {
  preferences: PreferenceItem[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Löst ein DisplayError-Objekt in der Aktive_Sprache auf (Req 12.6). */
function resolveError(
  t: (key: string, p?: Record<string, string | number>) => string,
  error: DisplayError,
): string {
  return error.kind === "key" ? t(error.key) : errorMessage(t, error.err);
}

// ---------------------------------------------------------------------------
// Notification preferences for a single group
// ---------------------------------------------------------------------------

function NotificationToggles({
  group,
  isFirst,
  emailEnabled,
  onToggleEmail,
}: {
  group: Group;
  isFirst: boolean;
  emailEnabled: boolean;
  onToggleEmail: (groupId: string, emailEnabled: boolean) => void;
}) {
  const { t } = useI18n();

  return (
    <Box sx={{ pt: isFirst ? 0 : 1.5, mt: isFirst ? 0 : 1.5 }}>
      {!isFirst && <Divider sx={{ mb: 1.5 }} />}
      <Typography fontWeight={600} mb={0.5} sx={{ wordBreak: "break-word" }}>
        {group.name}
      </Typography>
      <Stack>
        <FormControlLabel
          control={
            <Switch
              id={`email-${group.id}`}
              checked={emailEnabled}
              onChange={(e) => onToggleEmail(group.id, e.target.checked)}
            />
          }
          label={t("profile.notifications.email")}
        />
      </Stack>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// A single child row: displays the name with Rename (inline edit) + Remove.
// ---------------------------------------------------------------------------

function ChildRow({ child, isFirst }: { child: Child; isFirst: boolean }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(child.name);
  const [error, setError] = useState<DisplayError | null>(null);

  const renameMutation = useMutation({
    mutationFn: (name: string) =>
      apiClient.patch<ChildResponse>(`/api/children/${child.id}`, { name }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["children"] });
      setEditing(false);
    },
    onError: (err) => {
      setError({ kind: "api", err });
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => apiClient.delete<void>(`/api/children/${child.id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["children"] });
    },
    onError: (err) => {
      setError({ kind: "api", err });
    },
  });

  const busy = renameMutation.isPending || removeMutation.isPending;

  function startEdit() {
    setError(null);
    setDraft(child.name);
    setEditing(true);
  }

  function cancelEdit() {
    setError(null);
    setEditing(false);
    setDraft(child.name);
  }

  function handleSave() {
    setError(null);
    const name = draft.trim();
    if (name === "") {
      setError({ kind: "key", key: "profile.child.nameEmpty" });
      return;
    }
    if (name === child.name) {
      setEditing(false);
      return;
    }
    renameMutation.mutate(name);
  }

  return (
    <Box py={1}>
      {!isFirst && <Divider sx={{ mb: 1 }} />}
      {editing ? (
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <TextField
            type="text"
            size="small"
            aria-label={t("profile.child.renameAria", { name: child.name })}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setError(null);
            }}
            disabled={busy}
            autoFocus
            sx={{ flex: "1 1 140px", minWidth: 0 }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={busy}
            aria-busy={renameMutation.isPending}
            sx={{ minHeight: 44 }}
          >
            {renameMutation.isPending ? t("profile.child.saving") : t("common.save")}
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            onClick={cancelEdit}
            disabled={busy}
            sx={{ minHeight: 44 }}
          >
            {t("common.cancel")}
          </Button>
        </Stack>
      ) : (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          flexWrap="wrap"
        >
          <Typography fontWeight={500} sx={{ flex: "1 1 auto", wordBreak: "break-word" }}>
            {child.name}
          </Typography>
          <Stack direction="row" gap={0.75} flexWrap="wrap">
            <Button
              variant="outlined"
              size="small"
              onClick={startEdit}
              disabled={busy}
              sx={{ minHeight: 44 }}
            >
              {t("profile.child.rename")}
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => {
                setError(null);
                removeMutation.mutate();
              }}
              disabled={busy}
              aria-busy={removeMutation.isPending}
              sx={{ minHeight: 44 }}
            >
              {removeMutation.isPending
                ? t("profile.child.removing")
                : t("profile.child.remove")}
            </Button>
          </Stack>
        </Stack>
      )}

      {error !== null && (
        <Alert severity="error" role="alert" sx={{ mt: 1 }}>
          {resolveError(t, error)}
        </Alert>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// "My children" section: list + inline rename/remove + add control.
// ---------------------------------------------------------------------------

function ChildrenSection() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [newName, setNewName] = useState("");
  const [addError, setAddError] = useState<DisplayError | null>(null);

  const { data, isLoading, error } = useQuery<ChildrenResponse>({
    queryKey: ["children"],
    queryFn: () => apiClient.get<ChildrenResponse>("/api/children"),
  });

  const addMutation = useMutation({
    mutationFn: (name: string) =>
      apiClient.post<ChildResponse>("/api/children", { name }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["children"] });
      setNewName("");
    },
    onError: (err) => {
      setAddError({ kind: "api", err });
    },
  });

  function handleAdd() {
    setAddError(null);
    const name = newName.trim();
    if (name === "") {
      setAddError({ kind: "key", key: "profile.children.nameRequired" });
      return;
    }
    addMutation.mutate(name);
  }

  const children = data?.children ?? [];

  return (
    <Panel component="section" aria-labelledby="children-heading">
      <Typography id="children-heading" variant="h6" fontWeight={700} mb={0.5}>
        {t("profile.children.title")}
      </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {t("profile.children.note")}
        </Typography>

        {isLoading && (
          <Stack alignItems="center" sx={{ py: 2 }} aria-live="polite">
            <CircularProgress aria-label={t("profile.children.loading")} />
          </Stack>
        )}

        {!isLoading && error != null && (
          <Alert severity="error" role="alert">
            {errorMessage(t, error)}
          </Alert>
        )}

        {!isLoading && error == null && (
          <>
            {children.length > 0 ? (
              <Stack>
                {children.map((child, i) => (
                  <ChildRow key={child.id} child={child} isFirst={i === 0} />
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                {t("profile.children.empty")}
              </Typography>
            )}

            <Divider sx={{ my: 1.5 }} />
            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
              <TextField
                type="text"
                size="small"
                aria-label={t("profile.children.newNameAria")}
                placeholder={t("profile.children.newNamePlaceholder")}
                value={newName}
                onChange={(e) => {
                  setNewName(e.target.value);
                  setAddError(null);
                }}
                disabled={addMutation.isPending}
                sx={{ flex: "1 1 140px", minWidth: 0 }}
              />
              <Button
                variant="contained"
                onClick={handleAdd}
                disabled={addMutation.isPending}
                aria-busy={addMutation.isPending}
                sx={{ minHeight: 44 }}
              >
                {addMutation.isPending
                  ? t("profile.children.adding")
                  : t("profile.children.add")}
              </Button>
            </Stack>

            {addError !== null && (
              <Alert severity="error" role="alert" sx={{ mt: 1 }}>
                {resolveError(t, addError)}
              </Alert>
            )}
          </>
        )}
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Notifications section: loads per-group email preferences with a single query
// and renders a toggle per group. (Persisting changes is wired in task 6.2.)
// ---------------------------------------------------------------------------

function NotificationsSection({ groups }: { groups: Group[] }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  // Transient feedback for save/revert, mirroring WeeklyPlanView's shape.
  const [snack, setSnack] = useState<
    { variant: "success" | "error"; message: string } | null
  >(null);
  function showSnack(variant: "success" | "error", message: string) {
    setSnack({ variant, message });
  }

  const { data, isLoading, error } = useQuery<PreferencesResponse>({
    queryKey: ["notificationPreferences"],
    queryFn: () =>
      apiClient.get<PreferencesResponse>("/api/notification-preferences"),
  });

  // Per-group optimistic update of the email preference.
  const mutation = useMutation({
    mutationFn: ({ groupId, emailEnabled }: PreferenceItem) =>
      apiClient.put<{ preference: PreferenceItem }>(
        `/api/groups/${groupId}/notification-preferences`,
        { emailEnabled },
      ),
    onMutate: async (next: PreferenceItem) => {
      await queryClient.cancelQueries({
        queryKey: ["notificationPreferences"],
      });
      const previous = queryClient.getQueryData<PreferencesResponse>([
        "notificationPreferences",
      ]);
      queryClient.setQueryData<PreferencesResponse>(
        ["notificationPreferences"],
        (old) =>
          old && {
            preferences: old.preferences.map((p) =>
              p.groupId === next.groupId
                ? { ...p, emailEnabled: next.emailEnabled }
                : p,
            ),
          },
      );
      return { previous };
    },
    onError: (_err, _next, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(
          ["notificationPreferences"],
          ctx.previous,
        );
      }
      showSnack("error", t("profile.notifications.saveError"));
    },
    onSuccess: () => {
      showSnack("success", t("profile.notifications.saved"));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["notificationPreferences"] });
    },
  });

  function handleToggleEmail(groupId: string, emailEnabled: boolean) {
    mutation.mutate({ groupId, emailEnabled });
  }

  const preferences = data?.preferences ?? [];

  /** Resolved email preference for a group, defaulting to `true` if absent. */
  function emailEnabledFor(groupId: string): boolean {
    const match = preferences.find((p) => p.groupId === groupId);
    return match?.emailEnabled ?? true;
  }

  return (
    <Panel component="section" aria-labelledby="notif-heading">
      <Typography id="notif-heading" variant="h6" fontWeight={700} mb={0.5}>
        {t("profile.notifications.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {t("profile.notifications.note")}
      </Typography>

      {isLoading && (
        <Stack alignItems="center" sx={{ py: 2 }} aria-live="polite">
          <CircularProgress aria-label={t("profile.notifications.loading")} />
        </Stack>
      )}

      {!isLoading && error != null && (
        <Alert severity="error" role="alert">
          {errorMessage(t, error)}
        </Alert>
      )}

      {!isLoading &&
        error == null &&
        groups.map((group, i) => (
          <NotificationToggles
            key={group.id}
            group={group}
            isFirst={i === 0}
            emailEnabled={emailEnabledFor(group.id)}
            onToggleEmail={handleToggleEmail}
          />
        ))}

      <Snackbar
        open={snack != null}
        autoHideDuration={2200}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snack != null ? (
          <Alert
            severity={snack.variant}
            variant="filled"
            onClose={() => setSnack(null)}
            sx={{ width: "100%" }}
          >
            {snack.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// ProfilePage
// ---------------------------------------------------------------------------

export default function ProfilePage() {
  const { user } = useAuth();
  const { t } = useI18n();

  const { data, isLoading, error } = useQuery<GroupsResponse>({
    queryKey: ["groups"],
    queryFn: () => apiClient.get<GroupsResponse>("/api/groups"),
  });

  const groups = data?.groups ?? [];

  return (
    <>
      <AppHead
        title={t("profile.title")}
        backTo="/groups"
        backAriaLabel={t("profile.back")}
      />

      <PageWrap>
        {/* Identity */}
        {user !== null && (
          <Panel>
            <Typography fontWeight={700} fontSize={18} sx={{ wordBreak: "break-word" }}>
              {user.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word" }}>
              {user.email}
            </Typography>
          </Panel>
        )}

        {/* My children */}
        <ChildrenSection />

        {/* Loading state */}
        {isLoading && (
          <Stack alignItems="center" sx={{ py: 4 }} aria-live="polite">
            <CircularProgress aria-label={t("profile.groups.loading")} />
          </Stack>
        )}

        {/* Fetch error */}
        {!isLoading && error != null && (
          <Alert severity="error" role="alert">
            {errorMessage(t, error)}
          </Alert>
        )}

        {/* No groups */}
        {!isLoading && error == null && groups.length === 0 && (
          <Panel>
            <Typography variant="body2" color="text.secondary">
              {t("profile.groups.empty")}
            </Typography>
          </Panel>
        )}

        {/* Notification preferences */}
        {!isLoading && error == null && groups.length > 0 && (
          <NotificationsSection groups={groups} />
        )}
      </PageWrap>
    </>
  );
}
